// MACHINERY V2 (Stroje) — Prehľad: the EVENT MODEL (M2.16d, board 20
// "POSLEDNÉ UDALOSTI").
//
// Pure functions and types only — no React, no Redux, no i18n, no DOM. Every
// label this file needs is passed IN, exactly as tabs/tabFormat.ts does it, so
// the mapping is directly testable and cannot drift between two renderers.
//
// ============================================================
//  THE WIRE — PERSONALLY VERIFIED IN THE SERVER SOURCE
// ============================================================
//   GET /v1/gateway/machines/{machineId:guid}/events?since&limit   (limit=50 default)
//   GoToStockServer/src/GoToStock/MachineGateway/GoToStock.MachineGateway.WebAPI/
//     Areas/Customer/Controllers/CustomerMachineStatusController.cs:125-141
//   Class-level [Authorize(Roles = ALL_COMPANY_ROLES)] (:31) +
//   [RequireModulePermission(Modules.Machinery, PermissionLevel.Read)] (:32).
//   Rows come back NEWEST FIRST — MachineEventsRepository.GetRecentAsync orders
//   `OrderByDescending(x => x.Timestamp)` and clamps the limit to 1..500
//   (GoToStock.MachineGateway.EF/Repositories/MachineEventsRepository.cs:23-25).
//
// >>> AN EMPTY ARRAY IS THE NORMAL EMPTY STATE, NEVER AN ERROR. <<<
// The SEC-REQ-11 tenant guard returns `Ok(Array.Empty<MachineEventDto>())` for a
// cross-tenant OR nonexistent machine (:131-135), DELIBERATELY indistinguishable
// from "this machine has no events" — no 403, no 404, no existence disclosure.
// So `[]` carries three meanings at once and the UI may only render the mildest:
// "nothing to show". Anything that paints a failure on `[]` would show every
// eventless machine — i.e. nearly the whole fleet — a broken-looking panel.
// A REJECTED request is different and is treated as a failure by the hook.
//
// THE ENUM IS SERIALIZED AS AN INT, NOT A NAME. No JsonStringEnumConverter is
// registered for the customer path; the gateway's own test asserts the wire form
// verbatim — `json.Should().Contain("\"eventType\":9")`
// (GoToStock.MachineGateway.WebAPI.Tests/WatchChangeIngestTests.cs:142-147).
// So the numeric enum below is the contract, and an UNKNOWN number must render
// as a neutral row rather than crash a panel.
//
// WHY THIS IS NOT `internal/edgeMonitor/models/edgeMonitorModels.ts`. That module
// declares the same DTO and calls the same endpoint (edgeMonitorApi.ts:71-79) and
// is the prior art this file was written against — but it is OUTSIDE
// customer/machinery/v2, its enum is missing `WatchChange = 9` (the server has
// carried it since CONN-WATCH-PRIMITIVE), and v2's standing convention is to
// RESTATE a wire shape locally so the folder stays "one folder delete away".
// fleet/fleetTelemetry.ts:60-67 restates `FleetGatewayRow` for exactly this
// reason and says so. Same rule, same reason, one folder later.
//
// >>> THE TIMESTAMP IS NOT RESTATED, AND THAT IS THE ONE EXCEPTION. <<<
// `MachineEventDto.Timestamp` is a bare `DateTime` over a SQL `datetime2`
// column, so it arrives with NO `Z` and JS reads it as LOCAL — every row would
// print +2h in CEST. That correction is a property of the WIRE rather than of
// this screen, the edge monitor already reads the SAME endpoint and needs the
// SAME fix, and a shape can be restated harmlessly while a silent two-hour
// offset cannot. So the parser is imported from shared/utils/gatewayTime.ts,
// which carries the full server citation. One parser for one API.
import { parseUtcLoose } from "../../../../../shared/utils/gatewayTime";
import { ServiceReport } from "../../../models/ServiceReportsModel";
import { SR_TONE_PIP, srStatusTone } from "../tabs/srStatusTone";

/** Mirrors server `MachineEventType` (MachineEvent.cs:28-45), including the
 *  WatchChange member the edge-monitor copy predates. */
export enum PrehladEventType {
  StatusChanged = 1,
  AlarmRaised = 2,
  AlarmCleared = 3,
  ProgramStarted = 4,
  ProgramEnded = 5,
  PartCompleted = 6,
  AgentConnected = 7,
  AgentDisconnected = 8,
  WatchChange = 9,
}

/** Mirrors server `MachineEventDto` (Areas/Customer/Models/MachineStatusDto.cs:33-45). */
export interface PrehladEventDto {
  id: number;
  machineId: string;
  eventType: PrehladEventType;
  previousStatus?: number | null;
  newStatus?: number | null;
  alarmCode?: string | null;
  alarmMessage?: string | null;
  programName?: string | null;
  details?: string | null;
  /** ISO timestamp. */
  timestamp: string;
}

/** The pip colour classes the token sheet paints (machinery-v2-tokens.scss:222-231:
 *  `.pip`, `.pip-g`, `.pip-a`, `.pip-r`). `null` = no pip at all, which board 20
 *  draws for its middle row — an informational event is not a severity. */
export type PrehladPip = "g" | "a" | "r" | null;

/** One row of POSLEDNÉ UDALOSTI, whatever it was built from. */
export interface PrehladRow {
  key: string;
  /**
   * Epoch ms — the sort key for the merged list.
   *
   * ALWAYS FINITE. Both builders below coerce an unusable timestamp to 0
   * (`NO_TIME`) rather than letting NaN through: `mergePrehladRows` subtracts
   * two of these, and a NaN result makes the comparator INCONSISTENT — V8 may
   * then permute rows the comparison never touched, and the `.slice()` after it
   * would drop genuinely recent events.
   */
  at: number;
  text: string;
  pip: PrehladPip;
  /** Set on SR rows only; gateway events have nowhere to open. */
  onOpen?: () => void;
}

/**
 * Severity for a gateway event.
 *
 * `MachineStatusCode.Alarm` is 4 and `Running` is 1 — the numbering
 * fleet/fleetTelemetry.ts:79-80 records verbatim ("Running(1) Idle(2) Stopped(3)
 * Alarm(4) Offline(5) TelemetryStale(6)", mirroring server MachineStatus.cs).
 * A StatusChanged row is graded by the status it
 * moved TO; everything the list cannot grade gets no pip rather than a guessed
 * colour.
 */
export const eventPip = (e: PrehladEventDto): PrehladPip => {
  switch (e.eventType) {
    case PrehladEventType.AlarmRaised:
    case PrehladEventType.AgentDisconnected:
      return "r";
    case PrehladEventType.AlarmCleared:
    case PrehladEventType.ProgramStarted:
    case PrehladEventType.AgentConnected:
      return "g";
    case PrehladEventType.StatusChanged:
      if (e.newStatus === 4) return "r";
      if (e.newStatus === 1) return "g";
      return null;
    default:
      return null;
  }
};

/** i18n keys for the event-type labels. Closed map, so an unknown wire number
 *  falls to `unknown` instead of rendering a raw integer at the user. */
export const EVENT_LABEL_KEYS: Record<PrehladEventType, string> = {
  [PrehladEventType.StatusChanged]: "myMachinery:v2.profile.overview.event.statusChanged",
  [PrehladEventType.AlarmRaised]: "myMachinery:v2.profile.overview.event.alarmRaised",
  [PrehladEventType.AlarmCleared]: "myMachinery:v2.profile.overview.event.alarmCleared",
  [PrehladEventType.ProgramStarted]: "myMachinery:v2.profile.overview.event.programStarted",
  [PrehladEventType.ProgramEnded]: "myMachinery:v2.profile.overview.event.programEnded",
  [PrehladEventType.PartCompleted]: "myMachinery:v2.profile.overview.event.partCompleted",
  [PrehladEventType.AgentConnected]: "myMachinery:v2.profile.overview.event.agentConnected",
  [PrehladEventType.AgentDisconnected]: "myMachinery:v2.profile.overview.event.agentDisconnected",
  [PrehladEventType.WatchChange]: "myMachinery:v2.profile.overview.event.watchChange",
};

export const UNKNOWN_EVENT_LABEL_KEY = "myMachinery:v2.profile.overview.event.unknown";

/**
 * The detail that rides after the label, board 20's "· O_4451_PRIRUBA.NC".
 *
 * `details` is NOT used as a general fallback. On a WatchChange row it is a raw
 * JSON bag ({watchId, tag, direction, seq} — MachineEvent.cs:39-44), and on a
 * StatusChanged row the edge monitor treats it as a signal key
 * (edgeMonitor/components/Zone3EventStream.tsx:63-65). Neither is a sentence to
 * show a shop-floor user, so only the two typed, human fields are read.
 */
export const eventDetail = (e: PrehladEventDto): string => {
  const alarm = [e.alarmCode, e.alarmMessage].filter(Boolean).join(" · ");
  if (alarm) return alarm;
  return e.programName || "";
};

/** The sentinel for "this row carries no usable time" — see `PrehladRow.at`
 *  and the sort note on `mergePrehladRows`. */
const NO_TIME = 0;

/**
 * A gateway event becomes a row. `label` is already translated by the caller.
 *
 * The KEY is the server identity (`MachineEvent.Id`, a monotonic bigint PK), not
 * an array index: the list re-sorts after the SR merge, so an index key would
 * re-attach React state to the wrong row every time either source refreshes.
 *
 * >>> THE TIME IS PARSED AS UTC, NOT WITH A BARE `new Date(...)`. <<< The wire
 * carries no `Z` (see this file's header), so a bare parse reads the value as
 * LOCAL and every row on this card would be an hour or two out — enough to
 * print an alarm from ten minutes ago in the future, to bucket last night's
 * events as "today", and to interleave them wrongly against the SR rows, which
 * come from a different API. `parseUtcLoose` returns `null` rather than NaN, so
 * the coercion below is total.
 */
export const eventRow = (e: PrehladEventDto, label: string): PrehladRow => {
  const detail = eventDetail(e);
  const at = parseUtcLoose(e.timestamp);
  return {
    key: "ev-" + e.id,
    at: at === null ? NO_TIME : at,
    text: detail ? label + " · " + detail : label,
    pip: eventPip(e),
  };
};

/**
 * A service report becomes a row.
 *
 * >>> THE PIP TAKES THE SR STATUS, WHICH IS ALREADY DECIDED — AND IT NOW TAKES
 * IT FROM THE ONE PLACE THAT DECIDES IT. <<< Board 20 draws its SR row with a
 * RED pip; following the board literally would give the SAME report two
 * different colours on two tabs of one profile, because `SrStatusChip` grades
 * it green/amber one tap away. That deviation stands. What CHANGED at the
 * M2.16d review (item 1) is that this file used to re-implement the grading
 * rule inline — a comment invoking "one derivation per concept" directly above a
 * SECOND derivation of it. `tabs/srStatusTone.ts` is now the single rule and
 * both surfaces read it.
 *
 * >>> `openDate` IS NOT PARSED WITH `parseUtcLoose`, AND THAT IS DELIBERATE.
 * <<< It comes from the MACHINERY API, not the gateway, and the Servisné správy
 * tab renders the same field with a bare `new Date(value)`
 * (tabs/tabFormat.ts `formatTabDate`). One report must not appear to have two
 * different times on two tabs of one profile, so this half follows the SR tab.
 * If that API's timezone handling is ever corrected, both sites change together.
 */
export const srRow = (r: ServiceReport, onOpen?: () => void): PrehladRow => {
  const at = r.openDate ? new Date(r.openDate).getTime() : NO_TIME;
  return {
    key: "sr-" + r.number,
    // A non-empty but unparseable openDate yields NaN here — see `PrehladRow.at`.
    at: Number.isFinite(at) ? at : NO_TIME,
    text: r.number + " · " + r.title,
    pip: SR_TONE_PIP[srStatusTone(r.statusCode)],
    onOpen,
  };
};

/** How many rows board 20's card shows before it stops being a summary. */
export const PREHLAD_ROW_LIMIT = 8;

/**
 * The merged, newest-first list.
 *
 * Rows carrying no usable timestamp (`at` 0 — an SR with no openDate, an event
 * with an unparseable one) sort to the BOTTOM rather than to the top, which is
 * where a naive descending sort on 0 would put them relative to nothing. They
 * are not dropped: a real record with a missing date is still a real record.
 *
 * >>> THAT CLAIM IS NOW TRUE, AND IT WAS NOT (M2.16d review, F8). <<< It relied
 * on `at` being 0 for an unusable timestamp, but the event builder wrote
 * `new Date(...).getTime()` straight through, so an unparseable one produced
 * NaN. `b.at - a.at` on a NaN is NaN, i.e. neither `<0`, `0` nor `>0` — an
 * INCONSISTENT comparator, which V8 is free to answer by permuting rows the
 * comparison never examined, after which the `.slice()` below can drop
 * genuinely recent events. Both builders now coerce to 0, so every `at` reaching
 * this comparator is finite and the docblock above describes what happens.
 */
export const mergePrehladRows = (
  events: PrehladRow[],
  reports: PrehladRow[],
  limit: number = PREHLAD_ROW_LIMIT
): PrehladRow[] =>
  events
    // `concat` already returns a NEW array, so the sort below never mutates
    // either input — no defensive `.slice()` is needed and none is written.
    .concat(reports)
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);

/**
 * Board 20's compact stamp: bare clock today, "<yesterday> HH:mm" yesterday,
 * "DD.MM. HH:mm" before that.
 *
 * `now` is injected rather than read from the clock so the boundary is testable
 * and so one render cannot straddle midnight between two rows.
 */
export const prehladTimeLabel = (
  at: number,
  yesterdayLabel: string,
  now: Date = new Date()
): string => {
  if (!at || Number.isNaN(at)) return "—";
  const d = new Date(at);
  const pad = (n: number) => (n < 10 ? "0" + n : String(n));
  const clock = pad(d.getHours()) + ":" + pad(d.getMinutes());
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, now)) return clock;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(d, yesterday)) return yesterdayLabel + " " + clock;
  return pad(d.getDate()) + "." + pad(d.getMonth() + 1) + ". " + clock;
};
